import React from 'react'
import { renderToString } from 'react-dom/server'
import { Provider } from 'react-redux'
import { Switch } from 'react-router-dom'
import { ConnectedRouter } from 'connected-react-router'
import { ThemeProvider, ServerStyleSheet } from 'styled-components'
import { StickyContainer } from 'react-sticky'
import {
  ApolloProvider,
  ApolloProvider as ApolloHooksProvider,
} from '@apollo/client'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'

import configureApolloClient from './configureApolloClient'
import configureReduxStore from './typedRedux/configureStoreWithHistory'
import styledTheme from './styles/theme'
import GlobalStyle from './styles/GlobalStyles'

import CreateNewBucketList from './pages/bucketlist/CreateNewBucketList'
import ToastNotifier from './components/notifications/ToastNotifier'
import ToastProvider from './components/notifications/ToastProvider'
import AuthModal from './components/auth/AuthModal'
import SelectEscapeModal from './components/SelectEscape/Modal'
import JoinBooking from './components/bookings/Join'

import IsomorphicRoutes from './routes/IsomorphicRoutes'

import RailsContext, { RailsContextInterface } from './contexts/RailsContext'
import ServerSideContext from './contexts/ServerSideContext'
import TouchDeviceContext from './contexts/TouchDeviceContext'

import { isTouchDevice } from './components/utils/displayUtils'

import Navbar from './layout/Navbar'

import { fetchCurrentUser } from './actions/authActions'
import { fetchFeatureFlags } from './actions/featureFlagsActions'
import AccountVerificationBanner from './components/standalone/AccountVerificationBanner'
import VoucherBanner from './components/standalone/VoucherBanner'
import WildfiresBanner from './components/standalone/WildfiresBanner'
import { MediaQueryProvider } from './contexts/MediaQueryContext'
import EmailUnverified from './components/notifications/EmailUnverified'

import SetLanguage from './i18n/SetLanguage'

const client = configureApolloClient()

type ReduxStore = ReturnType<typeof configureReduxStore>['store']
type History = ReturnType<typeof configureReduxStore>['history']

const renderApp = (
  store: ReduxStore,
  history: History,
  railsContext: RailsContextInterface
) => {
  let ClientRoutes
  const clientSide = !railsContext.serverSide
  if (clientSide) {
    // eslint-disable-next-line global-require
    ClientRoutes = require('./routes/ClientRoutes').default
  }

  return (
    <RailsContext.Provider value={railsContext}>
      <ServerSideContext.Provider value={railsContext.serverSide}>
        <TouchDeviceContext.Provider value={isTouchDevice()}>
          <Provider store={store} key="provider">
            <ApolloProvider client={client}>
              <ApolloHooksProvider client={client}>
                <GoogleReCaptchaProvider
                  reCaptchaKey={railsContext.env.REACT_APP_RECAPTCHA_KEY}
                >
                  <MediaQueryProvider>
                    <ThemeProvider theme={styledTheme}>
                      <ToastProvider>
                        <SetLanguage>
                          <EmailUnverified />
                          <ConnectedRouter history={history}>
                            {!railsContext.embedded && <Navbar />}
                            <VoucherBanner />
                            <WildfiresBanner />
                            <StickyContainer>
                              <GlobalStyle />
                              <div>
                                <AccountVerificationBanner />
                                <ToastNotifier />
                                <AuthModal />
                                <JoinBooking />
                                <SelectEscapeModal />
                                <CreateNewBucketList />
                                <Switch>{ClientRoutes}</Switch>
                                <Switch>{IsomorphicRoutes}</Switch>
                              </div>
                            </StickyContainer>
                          </ConnectedRouter>
                        </SetLanguage>
                      </ToastProvider>
                    </ThemeProvider>
                  </MediaQueryProvider>
                </GoogleReCaptchaProvider>
              </ApolloHooksProvider>
            </ApolloProvider>
          </Provider>
        </TouchDeviceContext.Provider>
      </ServerSideContext.Provider>
    </RailsContext.Provider>
  )
}

export default (props: any, railsContext: RailsContextInterface) => {
  const { location, serverSide } = railsContext
  const { store, history } = configureReduxStore(props, {
    initialEntries: [location],
  })

  const renderedApp = renderApp(store, history, railsContext)
  let styleTags = null
  if (serverSide) {
    const sheet = new ServerStyleSheet()
    renderToString(sheet.collectStyles(renderedApp))
    styleTags = sheet.getStyleElement()
  }

  if (!serverSide) {
    store.dispatch(fetchCurrentUser())
    store.dispatch(fetchFeatureFlags())
  }

  return (
    <div>
      <div key="styled-component-styles">{styleTags}</div>
      <div key="react-app">{renderedApp}</div>
    </div>
  )
}
